import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, FileText, BarChart3, MessageSquare, CalendarDays,
  HeadphonesIcon, FolderOpen, UserCircle, Leaf, ArrowRight, ChevronRight,
  Clock, CheckCircle2, AlertCircle, Download, Eye, Phone, Video, MapPin,
  Mail, Search, Filter, TrendingUp, Shield, Zap, Euro, Bell, X,
  Home, Send, Paperclip, ChevronLeft
} from "lucide-react";
import { Button } from "../../components/ui/button";
import SimulationPage from "../SimulationPage";
import ExpertPlanForm from "../../components/ExpertPlanForm";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a41601fb-4bc6-4adf-a1fa-7db9141a0934/artifacts/7yebf3dk_eco-conseil-logo-D0cXQmFM.png";

/* ─── SECTION NAV ─── */
const SECTIONS = [
  { id: "resume", label: "Resume", icon: LayoutDashboard },
  { id: "projets", label: "Projets", icon: Home },
  { id: "devis", label: "Devis", icon: FileText },
  { id: "rapport", label: "Rapport", icon: BarChart3 },
  { id: "copilote", label: "Copilote", icon: MessageSquare },
  { id: "calendrier", label: "Calendrier", icon: CalendarDays },
  { id: "support", label: "Support", icon: HeadphonesIcon },
  { id: "documents", label: "Documents", icon: FolderOpen },
  { id: "profil", label: "Profil", icon: UserCircle },
];

/* ─── HELPERS ─── */
const statusColor = (s) => ({
  brouillon: "bg-gray-100 text-gray-600",
  en_preparation: "bg-blue-100 text-blue-700",
  envoye: "bg-amber-100 text-amber-700",
  accepte: "bg-emerald-100 text-emerald-700",
  refuse: "bg-red-100 text-red-700",
  archive: "bg-gray-200 text-gray-500",
  en_cours: "bg-emerald-100 text-emerald-700",
  termine: "bg-slate-100 text-slate-600",
  confirme: "bg-emerald-100 text-emerald-700",
  ouverte: "bg-blue-100 text-blue-700",
  resolue: "bg-emerald-100 text-emerald-700",
}[s] || "bg-gray-100 text-gray-600");

const statusLabel = (s) => ({
  brouillon: "Brouillon", en_preparation: "En preparation", envoye: "Envoye",
  accepte: "Accepte", refuse: "Refuse", archive: "Archive", en_cours: "En cours",
  termine: "Termine", confirme: "Confirme", ouverte: "Ouverte", resolue: "Resolue",
}[s] || s);

function SectionTitle({ icon: Icon, title, id, color = "text-emerald-600", bg = "bg-emerald-50" }) {
  return (
    <div id={id} className="scroll-mt-24 flex items-center gap-3 mb-6 pt-2">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}>
        <Icon size={20} className={color} />
      </div>
      <h2 className="font-bold text-xl text-gray-900" data-testid={`section-${id}`}>{title}</h2>
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN DASHBOARD PAGE
   ═══════════════════════════════════════ */
export default function DashboardPage() {
  const [activeSection, setActiveSection] = useState("resume");
  const [copiloteOpen, setCopiloteOpen] = useState(false);
  const [copiloteMsg, setCopiloteMsg] = useState("");
  const [showDevisForm, setShowDevisForm] = useState(false);
  const [showRapportForm, setShowRapportForm] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#F7FAF8]" data-testid="dashboard-page">
      {/* ─── TOP BAR ─── */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo">
                <img src={LOGO_URL} alt="Logo" className="h-9" />
              </Link>
              <span className="hidden sm:block text-xs text-gray-400 border-l pl-3 ml-1">Mon compte</span>
            </div>
            <Link to="/" className="text-sm text-gray-500 hover:text-emerald-600 flex items-center gap-1 transition-colors" data-testid="back-home">
              <ChevronLeft size={14} /> Retour au site
            </Link>
          </div>
        </div>
        {/* Section Nav */}
        <div className="border-t border-gray-100 bg-white/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <nav ref={navRef} className="flex gap-1 overflow-x-auto py-2 scrollbar-hide" data-testid="section-nav">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  data-testid={`nav-section-${s.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    activeSection === s.id
                      ? "bg-emerald-600 text-white shadow-sm"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  }`}
                >
                  <s.icon size={13} />
                  {s.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* ─── CONTENT ─── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-12 pb-32">

        {/* ═══ RESUME DU COMPTE ═══ */}
        <section id="resume">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-gradient-to-br from-[#1B3B2F] via-[#1B2A3B] to-[#0F2620] p-6 sm:p-8 text-white mb-6 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Leaf size={22} className="text-emerald-400" />
                </div>
                <div>
                  <h1 className="font-bold text-xl sm:text-2xl" data-testid="dashboard-welcome">Bienvenue sur votre espace</h1>
                  <p className="text-emerald-300 text-xs">Eco-Conseil-Habitat</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm max-w-lg mt-1">
                Suivez vos projets, consultez vos devis et rapports, planifiez vos rendez-vous — tout est centralise ici.
              </p>
            </div>
          </motion.div>

          {/* Quick access cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              { label: "Mes projets", desc: "Suivre l'avancement", icon: Home, color: "text-emerald-600", bg: "bg-emerald-50", target: "projets" },
              { label: "Demander un devis", desc: "Simulation gratuite", icon: FileText, color: "text-amber-600", bg: "bg-amber-50", target: "devis" },
              { label: "Prendre RDV", desc: "Avec un expert", icon: CalendarDays, color: "text-blue-600", bg: "bg-blue-50", target: "calendrier" },
              { label: "Support", desc: "Nous contacter", icon: HeadphonesIcon, color: "text-slate-600", bg: "bg-slate-50", target: "support" },
            ].map((card, i) => (
              <motion.button key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                onClick={() => scrollTo(card.target)}
                className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-emerald-200 transition-all text-left group" data-testid={`quick-${i}`}
              >
                <div className={`w-9 h-9 rounded-lg ${card.bg} flex items-center justify-center mb-2 group-hover:scale-105 transition-transform`}>
                  <card.icon size={16} className={card.color} />
                </div>
                <p className="font-semibold text-gray-900 text-sm">{card.label}</p>
                <p className="text-xs text-gray-500">{card.desc}</p>
              </motion.button>
            ))}
          </div>
        </section>

        {/* ═══ MES PROJETS ═══ */}
        <section id="projets">
          <SectionTitle icon={Home} title="Mes projets" id="projets" />
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <Home size={28} className="text-emerald-500" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2">Aucun projet en cours</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto mb-5">
              Lancez votre premier projet de renovation energetique en demandant un devis ou une analyse expert.
            </p>
            <div className="flex justify-center gap-3 flex-wrap">
              <Button onClick={() => scrollTo("devis")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm gap-2 shadow-md shadow-emerald-600/20" data-testid="projets-cta-devis">
                <FileText size={15} /> Demander un devis
              </Button>
              <Button variant="outline" onClick={() => scrollTo("rapport")} className="rounded-xl text-sm gap-2" data-testid="projets-cta-rapport">
                <BarChart3 size={15} /> Demander un rapport
              </Button>
            </div>
            {/* Timeline preview */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-4">Etapes de votre futur projet</p>
              <div className="hidden sm:flex items-center justify-between max-w-2xl mx-auto">
                {["Demande recue","Analyse","Devis recus","Devis accepte","Rapport expert","RDV planifie","Travaux","Reception","Termine"].map((label, i) => (
                  <div key={i} className="flex items-center flex-1 last:flex-none">
                    <div className="flex flex-col items-center">
                      <div className="w-7 h-7 rounded-full bg-gray-100 border-2 border-gray-200 flex items-center justify-center text-[9px] font-bold text-gray-400">{i + 1}</div>
                      <span className="text-[9px] mt-1 text-gray-400 text-center max-w-[60px] leading-tight">{label}</span>
                    </div>
                    {i < 8 && <div className="h-0.5 flex-1 mx-0.5 bg-gray-200 mt-[-16px]" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══ MES DEVIS ═══ */}
        <section id="devis">
          <SectionTitle icon={FileText} title="Mes devis" id="devis" color="text-amber-600" bg="bg-amber-50" />
          <AnimatePresence mode="wait">
            {!showDevisForm ? (
              <motion.div key="devis-cta" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center" data-testid="devis-cta-block"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-600/20">
                  <FileText size={28} className="text-white" />
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">Obtenez votre devis personnalise</h3>
                <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
                  Remplissez le formulaire pour recevoir un devis precis et detaille pour vos travaux de renovation energetique.
                </p>
                <Button
                  onClick={() => setShowDevisForm(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-8 py-3 text-sm font-semibold gap-2 shadow-lg shadow-emerald-600/20 hover:scale-[1.02] transition-all"
                  data-testid="btn-demander-devis"
                >
                  <FileText size={16} /> Demander un Devis
                </Button>
                <div className="flex justify-center gap-6 mt-6">
                  {[
                    { icon: Clock, t: "Reponse en 2h" },
                    { icon: CheckCircle2, t: "Artisans RGE" },
                    { icon: Euro, t: "Aides incluses" },
                  ].map((b, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <b.icon size={13} className="text-emerald-500" />{b.t}
                    </div>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div key="devis-form" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-500">Remplissez le formulaire ci-dessous pour obtenir votre devis.</p>
                  <Button variant="outline" size="sm" onClick={() => setShowDevisForm(false)} className="gap-1 text-xs rounded-lg" data-testid="btn-close-devis-form">
                    <X size={13} /> Fermer
                  </Button>
                </div>
                <SimulationPage embedded={true} />
              </motion.div>
            )}
          </AnimatePresence>
        </section>

        {/* ═══ RAPPORT ANALYSE-EXPERT ═══ */}
        <section id="rapport">
          <SectionTitle icon={BarChart3} title="Rapport analyse-expert" id="rapport" color="text-blue-600" bg="bg-blue-50" />
          <AnimatePresence mode="wait">
            {!showRapportForm ? (
              <motion.div key="rapport-cta" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center" data-testid="rapport-cta-block"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-600/20">
                  <BarChart3 size={28} className="text-white" />
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">Demandez votre rapport expert</h3>
                <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
                  Notre equipe d'experts realise une analyse energetique complete de votre logement avec recommandations personnalisees.
                </p>
                <Button
                  onClick={() => setShowRapportForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 py-3 text-sm font-semibold gap-2 shadow-lg shadow-blue-600/20 hover:scale-[1.02] transition-all"
                  data-testid="btn-demander-rapport"
                >
                  <BarChart3 size={16} /> Demander un Rapport
                </Button>
                <div className="flex justify-center gap-6 mt-6">
                  {[
                    { icon: Eye, t: "Precision 75% a distance" },
                    { icon: MapPin, t: "100% sur place" },
                    { icon: Shield, t: "Experts certifies" },
                  ].map((b, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <b.icon size={13} className="text-blue-500" />{b.t}
                    </div>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div key="rapport-form" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-500">Remplissez le formulaire d'audit pour votre rapport expert.</p>
                  <Button variant="outline" size="sm" onClick={() => setShowRapportForm(false)} className="gap-1 text-xs rounded-lg" data-testid="btn-close-rapport-form">
                    <X size={13} /> Fermer
                  </Button>
                </div>
                <ExpertPlanForm />
              </motion.div>
            )}
          </AnimatePresence>
        </section>

        {/* ═══ COPILOTE EXPERT RGE ═══ */}
        <section id="copilote">
          <SectionTitle icon={MessageSquare} title="Copilote Expert RGE 7j/7" id="copilote" color="text-teal-600" bg="bg-teal-50" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Clock, t: "Disponible 7j/7", d: "Notre equipe d'experts RGE repond a vos questions tous les jours." },
              { icon: Shield, t: "Experts certifies RGE", d: "Conseillers Reconnu Garant de l'Environnement." },
              { icon: FileText, t: "Verification des devis", d: "Faites verifier vos devis artisans par un expert." },
              { icon: Euro, t: "Aides financieres", d: "MaPrimeRenov, CEE, eco-pret : on vous guide." },
              { icon: TrendingUp, t: "Suivi de projet", d: "Accompagnement personnalise etape par etape." },
              { icon: Zap, t: "Reponse rapide", d: "Obtenez une reponse en moins de 2h." },
            ].map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="bg-white border border-gray-100 rounded-xl p-4 hover:border-teal-200 hover:shadow-md transition-all" data-testid={`copilote-card-${i}`}
              >
                <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center mb-2"><f.icon size={16} className="text-teal-600" /></div>
                <h3 className="font-semibold text-gray-900 text-sm">{f.t}</h3>
                <p className="text-gray-500 text-xs mt-1">{f.d}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-4 bg-teal-600 rounded-xl p-5 text-center text-white">
            <p className="font-semibold mb-1">Besoin d'un avis expert ?</p>
            <p className="text-teal-100 text-xs mb-3">Utilisez le bouton vert en bas a droite pour demarrer une conversation.</p>
            <Button className="bg-white text-teal-700 hover:bg-teal-50 rounded-xl text-xs gap-1" onClick={() => setCopiloteOpen(true)}>
              <MessageSquare size={14} /> Parler au copilote
            </Button>
          </div>
        </section>

        {/* ═══ CALENDRIER RDV ═══ */}
        <section id="calendrier">
          <SectionTitle icon={CalendarDays} title="Calendrier RDV" id="calendrier" color="text-slate-700" bg="bg-slate-100" />
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center">
            <CalendarDays size={32} className="text-slate-400 mx-auto mb-3" />
            <h3 className="font-bold text-gray-900 text-lg mb-2">Planifier un rendez-vous</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">Choisissez le type de rendez-vous qui vous convient et contactez nos experts.</p>
            <div className="flex justify-center gap-3 flex-wrap">
              {[
                { icon: Phone, l: "Appel telephonique", d: "30 min" },
                { icon: Video, l: "Visio-conference", d: "45 min" },
                { icon: MapPin, l: "Visite a domicile", d: "1h30" },
              ].map((t, i) => (
                <a key={i} href="mailto:contact-expert@eco-conseil-habitat.com" data-testid={`rdv-type-${i}`}>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer group w-44 text-center">
                    <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center mx-auto mb-2 group-hover:bg-emerald-100 transition-colors">
                      <t.icon size={18} className="text-slate-600 group-hover:text-emerald-600 transition-colors" />
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">{t.l}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t.d}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ SUPPORT ASSISTANCE ═══ */}
        <section id="support">
          <SectionTitle icon={HeadphonesIcon} title="Support assistance" id="support" color="text-purple-600" bg="bg-purple-50" />
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            {[
              { icon: Mail, t: "Email", v: "contact-expert@eco-conseil-habitat.com", d: "Reponse sous 24h", href: "mailto:contact-expert@eco-conseil-habitat.com", bg: "bg-emerald-50", color: "text-emerald-600" },
              { icon: Phone, t: "Telephone", v: "Prendre RDV", d: "Lun-Sam 9h-19h", href: "#calendrier", bg: "bg-blue-50", color: "text-blue-600" },
              { icon: MessageSquare, t: "Copilote", v: "Chat en direct", d: "Disponible 7j/7", bg: "bg-teal-50", color: "text-teal-600", action: () => setCopiloteOpen(true) },
            ].map((c, i) => (
              <a key={i} href={c.href} onClick={c.action} data-testid={`support-method-${i}`}
                className={`block ${c.bg} rounded-xl p-4 border border-gray-100 hover:shadow-md transition-all cursor-pointer group`}
              >
                <c.icon size={18} className={c.color} />
                <p className="font-semibold text-gray-900 text-sm mt-2">{c.t}</p>
                <p className={`text-xs ${c.color} font-medium mt-0.5`}>{c.v}</p>
                <p className="text-xs text-gray-500 mt-1">{c.d}</p>
              </a>
            ))}
          </div>
          {/* Contact form */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="font-semibold text-gray-800 text-sm mb-3">Envoyer un message</h3>
            <div className="space-y-3">
              <input type="text" placeholder="Objet de votre demande" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" data-testid="support-subject" />
              <textarea placeholder="Decrivez votre demande..." className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm h-24 resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" data-testid="support-message" />
              <div className="flex items-center justify-between">
                <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"><Paperclip size={13} /> Joindre un fichier</button>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs gap-1" data-testid="support-send"><Send size={13} /> Envoyer</Button>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ MES DOCUMENTS ═══ */}
        <section id="documents">
          <SectionTitle icon={FolderOpen} title="Mes documents" id="documents" color="text-slate-700" bg="bg-slate-100" />
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
              <FolderOpen size={24} className="text-slate-400" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2">Aucun document</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto mb-5">
              Vos devis, rapports et documents de suivi apparaitront ici au fur et a mesure de l'avancement de votre projet.
            </p>
            <div className="flex justify-center gap-3 flex-wrap">
              <Button onClick={() => scrollTo("devis")} variant="outline" className="rounded-xl text-xs gap-1" data-testid="docs-cta-devis">
                <FileText size={13} /> Demander un devis
              </Button>
              <Button onClick={() => scrollTo("rapport")} variant="outline" className="rounded-xl text-xs gap-1" data-testid="docs-cta-rapport">
                <BarChart3 size={13} /> Demander un rapport
              </Button>
            </div>
          </div>
        </section>

        {/* ═══ MON PROFIL ═══ */}
        <section id="profil">
          <SectionTitle icon={UserCircle} title="Mon profil" id="profil" color="text-gray-600" bg="bg-gray-100" />
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 shadow-sm">
            <p className="text-xs text-gray-500 mb-4 flex items-center gap-1"><Shield size={12} /> Les modifications de profil seront disponibles avec l'espace securise.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { l: "Nom", ph: "Votre nom" },
                { l: "Prenom", ph: "Votre prenom" },
                { l: "Email", ph: "votre@email.com", type: "email" },
                { l: "Telephone", ph: "06 12 34 56 78", type: "tel" },
              ].map((f, i) => (
                <div key={i} className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">{f.l}</label>
                  <input type={f.type || "text"} placeholder={f.ph} disabled
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
                    data-testid={`profil-${f.l.toLowerCase()}`} />
                </div>
              ))}
            </div>
            <div className="mt-5 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <p className="text-sm text-emerald-800 font-medium">Espace securise bientot disponible</p>
              <p className="text-xs text-emerald-600 mt-1">La gestion complete de votre profil, mot de passe et preferences sera activee prochainement.</p>
            </div>
          </div>
        </section>
      </main>

      {/* ─── FLOATING COPILOTE BUTTON ─── */}
      <button
        onClick={() => setCopiloteOpen(!copiloteOpen)}
        data-testid="floating-copilote"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-xl shadow-emerald-600/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
      >
        {copiloteOpen ? <X size={22} /> : <MessageSquare size={22} />}
      </button>

      {/* ─── COPILOTE PANEL ─── */}
      {copiloteOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="fixed bottom-24 right-6 z-50 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
          data-testid="copilote-panel"
        >
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 text-white">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><MessageSquare size={16} /></div>
              <div>
                <p className="font-semibold text-sm">Copilote Expert RGE</p>
                <p className="text-emerald-100 text-xs">Disponible 7j/7</p>
              </div>
            </div>
          </div>
          <div className="p-4 h-48 bg-gray-50 flex items-center justify-center">
            <p className="text-xs text-gray-400 text-center">Posez votre question sur la renovation energetique, les aides ou votre projet.</p>
          </div>
          <div className="p-3 border-t border-gray-100 flex gap-2">
            <input
              type="text"
              value={copiloteMsg}
              onChange={(e) => setCopiloteMsg(e.target.value)}
              placeholder="Votre question..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500"
              data-testid="copilote-input"
            />
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-3" data-testid="copilote-send">
              <Send size={14} />
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
